import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Copy,
  Play,
  Sparkles,
  Pencil,
  Trash2,
  FolderPlus,
  Star,
  GitFork,
  AlertTriangle,
  Share2,
  History,
  Upload,
  Paperclip,
  X
} from 'lucide-react';
import { apiClient } from '../apiClient';
import { PUBLIC_API_BASE } from '../config';
import Modal from '../components/Modal';
import SelectMenu from '../components/SelectMenu';
import ShareAccessModal from '../components/ShareAccessModal';
import VisibilityIndicator from '../components/VisibilityIndicator';
import { sanitizeRichText } from '../utils/richText';
import { buildProfilePath } from '../utils/profileRoutes';
import Breadcrumbs from '../components/Breadcrumbs';
import PromptAttachmentList from '../components/PromptAttachmentList';

function PromptDetail() {
  const { promptId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [prompt, setPrompt] = useState(null);
  const [inputValues, setInputValues] = useState({});
  const [completedPrompt, setCompletedPrompt] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [showDelete, setShowDelete] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showAddToFolder, setShowAddToFolder] = useState(false);
  const [showApiInfo, setShowApiInfo] = useState(false);
  const [projects, setProjects] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState('');
  const [userRating, setUserRating] = useState(null);
  const [hoverRating, setHoverRating] = useState(null);
  const [favorited, setFavorited] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [showShare, setShowShare] = useState(false);
  const [savedOutputs, setSavedOutputs] = useState([]);
  const [selectedSaveId, setSelectedSaveId] = useState('');
  const [savingOutput, setSavingOutput] = useState(false);
  const [showClearInputs, setShowClearInputs] = useState(false);
  const [promptHistory, setPromptHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedHistoryId, setSelectedHistoryId] = useState('');
  const [activeVersion, setActiveVersion] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const fileInputRef = useRef(null);

  const clearPendingFiles = useCallback(() => {
    setPendingFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const applySavedOutput = useCallback((output) => {
    if (!output) return;
    const values = output.variable_values || {};
    setInputValues(values);
    clearPendingFiles();
    if (output.rendered_output) {
      setCompletedPrompt(output.rendered_output);
    } else {
      setCompletedPrompt('');
    }
    setMessage('Saved inputs loaded. You can copy or run now.');
  }, [clearPendingFiles]);

  const fetchPrompt = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiClient.get(`/api/prompts/${promptId}`);
      setPrompt(data);

      const defaults = {};
      (data.variables || []).forEach((variable) => {
        if (variable.input_type === 'multiple_choice') {
          defaults[variable.variable_name] = [];
          return;
        }
        defaults[variable.variable_name] = variable.default_value || '';
      });
      setInputValues(defaults);
    } catch (err) {
      setError(err.message || 'Unable to load prompt');
    } finally {
      setLoading(false);
    }
  }, [promptId]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!mounted) return;
      await fetchPrompt();
    };
    load();
    return () => {
      mounted = false;
    };
  }, [fetchPrompt]);

  useEffect(() => {
    let mounted = true;
    const fetchUser = async () => {
      setLoadingUser(true);
      try {
        const data = await apiClient.get('/api/auth/me');
        if (mounted) {
          setCurrentUser(data);
        }
      } catch (err) {
        // ignore - handled by protected route
      } finally {
        if (mounted) {
          setLoadingUser(false);
        }
      }
    };

    fetchUser();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!promptId) return;
    const loadMeta = async () => {
      try {
        const [ratingData, favData] = await Promise.all([
          apiClient.get(`/api/ratings/${promptId}`),
          apiClient.get('/api/favorites', { prompt_id: promptId })
        ]);
        setUserRating(ratingData.rating || null);
        setFavorited(!!favData.favorited);
      } catch (err) {
        // ignore
      }
    };
    loadMeta();
  }, [promptId]);

  useEffect(() => {
    if (!promptId) return;
    const loadSaved = async () => {
      try {
        const data = await apiClient.get(`/api/outputs/${promptId}`);
        setSavedOutputs(Array.isArray(data) ? data : []);
      } catch (err) {
        // ignore
      }
    };
    loadSaved();
  }, [promptId]);

  useEffect(() => {
    if (!promptId) return;
    const loadHistory = async () => {
      try {
        const data = await apiClient.get(`/api/prompts/${promptId}/history`);
        setPromptHistory(Array.isArray(data) ? data : []);
      } catch (err) {
        setPromptHistory([]);
      }
    };
    loadHistory();
  }, [promptId]);

  const effectivePrompt = useMemo(() => {
    if (!activeVersion) return prompt;
    return {
      ...prompt,
      name: activeVersion.name || prompt?.name,
      description: activeVersion.description || prompt?.description,
      prompt_template: activeVersion.prompt_template || prompt?.prompt_template,
      variables: activeVersion.variables_json || prompt?.variables || []
    };
  }, [prompt, activeVersion]);

  useEffect(() => {
    if (!location.state?.savedOutputId) return;
    if (!savedOutputs.length) return;
    const target = savedOutputs.find((output) => output.output_id === location.state.savedOutputId);
    if (target) {
      setSelectedSaveId(target.output_id);
      applySavedOutput(target);
    }
  }, [applySavedOutput, location.state, savedOutputs]);

  useEffect(() => {
    if (!showAddToFolder) return;
    const loadProjects = async () => {
      try {
        const data = await apiClient.get('/api/projects');
        setProjects(data);
      } catch (err) {
        setMessage('Unable to load projects.');
      }
    };
    loadProjects();
  }, [showAddToFolder]);

  useEffect(() => {
    if (!selectedProjectId) {
      setFolders([]);
      setSelectedFolderId('');
      return;
    }
    const loadFolders = async () => {
      try {
        const data = await apiClient.get('/api/folders', { project_id: selectedProjectId });
        setFolders(data);
      } catch (err) {
        setMessage('Unable to load folders.');
      }
    };
    loadFolders();
  }, [selectedProjectId]);

  const variableMap = useMemo(() => {
    const map = new Map();
    (effectivePrompt?.variables || []).forEach((variable) => {
      map.set(variable.variable_name, variable);
    });
    return map;
  }, [effectivePrompt]);

  const resolveValue = (variableName) => {
    const variable = variableMap.get(variableName);
    const rawValue = inputValues[variableName];
    if (Array.isArray(rawValue)) {
      return rawValue.join(', ');
    }
    if (rawValue !== undefined && rawValue !== null && String(rawValue).length > 0) {
      return String(rawValue);
    }
    if (variable?.default_value) {
      return variable.default_value;
    }
    return '';
  };

  const buildDefaultInputs = (promptData) => {
    const defaults = {};
    (promptData?.variables || []).forEach((variable) => {
      if (variable.input_type === 'multiple_choice') {
        defaults[variable.variable_name] = [];
        return;
      }
      defaults[variable.variable_name] = variable.default_value || '';
    });
    return defaults;
  };

  const mergePendingFiles = (fileList) => {
    const nextFiles = Array.from(fileList || []);
    if (!nextFiles.length) return;
    setPendingFiles((prev) => {
      const seen = new Set(prev.map((item) => `${item.name}-${item.size}-${item.lastModified}`));
      const additions = nextFiles.filter((item) => {
        const key = `${item.name}-${item.size}-${item.lastModified}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      return [...prev, ...additions];
    });
  };

  const removePendingFile = (index) => {
    setPendingFiles((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
  };

  const replaceSavedOutput = useCallback((nextOutput) => {
    if (!nextOutput?.output_id) return;
    setSavedOutputs((prev) => {
      const existing = prev.some((item) => item.output_id === nextOutput.output_id);
      if (!existing) {
        return [nextOutput, ...prev];
      }
      return prev.map((item) => (item.output_id === nextOutput.output_id ? nextOutput : item));
    });
  }, []);

  const uploadFilesToOutput = useCallback(async (outputId, filesToUpload) => {
    const uploads = await Promise.allSettled(
      filesToUpload.map(async (currentFile) => {
        const formData = new FormData();
        formData.append('file', currentFile);
        return apiClient.request(`/api/outputs/${outputId}/files`, {
          method: 'POST',
          body: formData,
          timeoutMs: 120000
        });
      })
    );

    const succeeded = uploads
      .filter((result) => result.status === 'fulfilled')
      .map((result) => result.value.data);
    const failed = uploads.filter((result) => result.status === 'rejected');
    return { succeeded, failed };
  }, []);

  const handleCompletePrompt = () => {
    if (!effectivePrompt?.prompt_template) return;
    const filled = effectivePrompt.prompt_template.replace(/\{\{([^}]+)\}\}/g, (match, inner) => {
      const key = inner.trim();
      const value = resolveValue(key);
      return value || match;
    });
    setCompletedPrompt(filled);
    setMessage('Prompt ready to copy or run.');
  };

  const handleClearInputs = () => {
    setInputValues(buildDefaultInputs(effectivePrompt));
    setCompletedPrompt('');
    setSelectedSaveId('');
    clearPendingFiles();
    setMessage('Inputs cleared.');
  };

  const handleCopy = async () => {
    if (!completedPrompt) return;
    try {
      await navigator.clipboard.writeText(completedPrompt);
      setMessage('Copied to clipboard.');
    } catch (err) {
      setMessage('Copy failed. Please try again.');
    }
  };

  const handleRun = async () => {
    if (!completedPrompt) {
      setMessage('Complete the prompt first.');
      return;
    }
    try {
      await navigator.clipboard.writeText(completedPrompt);
      setMessage('Prompt copied. Paste it into your preferred AI tool to run.');
    } catch (err) {
      setMessage('Unable to copy prompt. Please copy manually from the output box.');
    }
  };

  const breadcrumbItems = useMemo(() => {
    if (location.state?.breadcrumbs?.length) {
      const base = [...location.state.breadcrumbs];
      if (prompt?.name) {
        base.push({ label: prompt.name });
      }
      return base;
    }
    return [
      { label: 'Profile', to: '/profile' },
      { label: prompt?.name || 'Prompt' }
    ];
  }, [location.state, prompt?.name]);

  const handleBack = () => {
    const fallback = location.state?.backTo || location.state?.breadcrumbs?.slice(-1)?.[0]?.to;
    if (fallback) {
      navigate(fallback);
      return;
    }
    navigate(-1);
  };

  const descriptionHtml = sanitizeRichText(effectivePrompt?.description || '');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen p-6">
        <div className="card-glass max-w-3xl mx-auto text-center">
          <h1 className="text-2xl font-bold mb-2">Prompt unavailable</h1>
          <p className="text-gray-400 mb-6">{error}</p>
          <button className="btn-primary" onClick={() => navigate('/profile')}>
            Back to Profile
          </button>
        </div>
      </div>
    );
  }

  const isOwner = !!(currentUser && prompt && currentUser.user_id === prompt.user_id);
  const isPublic = prompt?.visibility === 'public';

  const handleToggleFavorite = async () => {
    if (!promptId) return;
    setActionLoading(true);
    try {
      const data = await apiClient.post('/api/favorites', { prompt_id: promptId });
      setFavorited(!!data.favorited);
    } catch (err) {
      setMessage(err.message || 'Favorite failed.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveOutput = async () => {
    if (!promptId || !completedPrompt) return;
    setSavingOutput(true);
    setUploadingFiles(true);
    try {
      const saved = await apiClient.post('/api/outputs', {
        prompt_id: promptId,
        variable_values: inputValues,
        rendered_output: completedPrompt
      });
      let nextSaved = saved;
      if (pendingFiles.length) {
        const { succeeded, failed } = await uploadFilesToOutput(saved.output_id, pendingFiles);
        nextSaved = {
          ...saved,
          attachments: succeeded,
          attachment_count: succeeded.length
        };
        clearPendingFiles();
        setMessage(
          failed.length
            ? `Saved prompt, but ${failed.length} file${failed.length === 1 ? '' : 's'} failed to upload.`
            : 'Saved to your Saved Prompts with private files attached.'
        );
      } else {
        setMessage('Saved to your Saved Prompts.');
      }
      replaceSavedOutput(nextSaved);
      setSelectedSaveId(nextSaved.output_id || '');
    } catch (err) {
      setMessage(err.message || 'Save failed.');
    } finally {
      setSavingOutput(false);
      setUploadingFiles(false);
    }
  };

  const handleUploadToSelectedSave = async () => {
    if (!selectedSaveId || !pendingFiles.length) return;
    setUploadingFiles(true);
    try {
      const { succeeded, failed } = await uploadFilesToOutput(selectedSaveId, pendingFiles);
      const current = savedOutputs.find((item) => item.output_id === selectedSaveId);
      const mergedOutput = {
        ...(current || {}),
        attachments: [...(current?.attachments || []), ...succeeded],
        attachment_count: (current?.attachment_count || 0) + succeeded.length
      };
      replaceSavedOutput(mergedOutput);
      clearPendingFiles();
      setMessage(
        failed.length
          ? `Uploaded ${succeeded.length} file${succeeded.length === 1 ? '' : 's'}, but ${failed.length} failed.`
          : 'Private files attached to your saved prompt.'
      );
    } catch (err) {
      setMessage(err.message || 'File upload failed.');
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleDeleteAttachment = async (attachment) => {
    if (!selectedSaveId || !attachment?.file_id) return;
    try {
      await apiClient.del(`/api/outputs/${selectedSaveId}/files/${attachment.file_id}`);
      const current = savedOutputs.find((item) => item.output_id === selectedSaveId);
      const remaining = (current?.attachments || []).filter((item) => item.file_id !== attachment.file_id);
      replaceSavedOutput({
        ...(current || {}),
        attachments: remaining,
        attachment_count: remaining.length
      });
      setMessage('Attachment removed.');
    } catch (err) {
      setMessage(err.message || 'Unable to delete attachment.');
    }
  };

  const formatSavedDate = (value) => {
    if (!value) return 'Saved output';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Saved output';
    return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  };

  const formatHistoryLabel = (item) => {
    const stamp = formatSavedDate(item?.created_at);
    return `${item?.action || 'UPDATED'} - ${stamp}`;
  };

  const handleSubmitRating = async (ratingValue) => {
    if (!promptId) return;
    setActionLoading(true);
    try {
      await apiClient.post('/api/ratings', { prompt_id: promptId, rating: ratingValue });
      setUserRating(ratingValue);
      await fetchPrompt();
    } catch (err) {
      setMessage(err.message || 'Rating failed.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmitReport = async () => {
    if (!promptId || !reportReason.trim()) return;
    setActionLoading(true);
    try {
      await apiClient.post('/api/reports', { prompt_id: promptId, reason: reportReason.trim() });
      setReportReason('');
      setShowReport(false);
      setMessage('Report submitted. Thank you for keeping Rebel Prompts safe.');
    } catch (err) {
      setMessage(err.message || 'Report failed.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!promptId) return;
    setActionLoading(true);
    try {
      await apiClient.del(`/api/prompts/${promptId}`);
      navigate('/profile');
    } catch (err) {
      setMessage(err.message || 'Delete failed.');
    } finally {
      setActionLoading(false);
      setShowDelete(false);
    }
  };

  const handleAddToFolder = async () => {
    if (!promptId) return;
    setActionLoading(true);
    try {
      const forked = await apiClient.post(`/api/prompts/${promptId}/fork`);

      if (selectedProjectId || selectedFolderId) {
        await apiClient.patch(`/api/prompts/${forked.prompt_id}`, {
          project_id: selectedProjectId || null,
          folder_id: selectedFolderId || null
        });
      }

      navigate(`/prompts/${forked.prompt_id}`);
    } catch (err) {
      setMessage(err.message || 'Unable to add prompt.');
    } finally {
      setActionLoading(false);
      setShowAddToFolder(false);
    }
  };

  const savedOptions = savedOutputs.map((output) => ({
    value: output.output_id,
    label: output.name || formatSavedDate(output.created_at),
    description: output.name ? formatSavedDate(output.created_at) : 'Saved output'
  }));
  const selectedSavedOutput = savedOutputs.find((output) => output.output_id === selectedSaveId) || null;

  const apiBase = PUBLIC_API_BASE.replace(/\/$/, '');
  const templateEndpoint = `${apiBase}/api/v1/hosting/prompts/${promptId}/template`;
  const outputsEndpoint = `${apiBase}/api/v1/hosting/prompts/${promptId}/outputs`;

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="prompt-breadcrumbs">
          <button onClick={handleBack} className="text-gray-400 hover:text-white">
            {'<-'} Back
          </button>
          <Breadcrumbs items={breadcrumbItems} />
        </div>

        <div className="card-glass prompt-hero">
          <div className="prompt-hero-header">
            <div className="prompt-hero-main">
              <h1 className="text-3xl font-bold mb-3">{effectivePrompt?.name}</h1>
              {descriptionHtml ? (
                <div className="rich-text text-gray-200" dangerouslySetInnerHTML={{ __html: descriptionHtml }} />
              ) : (
                <p className="text-gray-400">No description provided.</p>
              )}
            </div>
          </div>
          <div className="prompt-meta-row">
            {prompt?.creator && (
              <Link className="creator-line" to={buildProfilePath(prompt.creator)}>
                <div className="creator-avatar">
                  {prompt.creator.avatar_url ? (
                    <img src={prompt.creator.avatar_url} alt={prompt.creator.username || prompt.creator.name || 'User'} />
                  ) : (
                    <span>{(prompt.creator.username || prompt.creator.name || 'RP').slice(0, 2).toUpperCase()}</span>
                  )}
                </div>
                <div>
                  <div className="creator-label">Created by</div>
                  <div className="creator-name">
                    {prompt.creator.username || prompt.creator.name || 'Rebel Creator'}
                  </div>
                </div>
              </Link>
            )}
            <div className="prompt-meta-actions">
              <VisibilityIndicator visibility={prompt?.visibility || 'private'} />
              <div className="badge">{prompt?.visibility || 'private'}</div>
              {!loadingUser && isOwner && (
                <>
                  <button
                    className="btn-secondary btn-compact flex items-center gap-2"
                    onClick={() => navigate(`/prompts/${promptId}/edit`)}
                  >
                    <Pencil className="w-4 h-4" /> Edit Prompt
                  </button>
                  <button
                    className="btn-secondary btn-compact flex items-center gap-2"
                    onClick={() => setShowDelete(true)}
                  >
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                </>
              )}
              {!loadingUser && !isOwner && isPublic && (
                <button
                  className="btn-secondary btn-compact flex items-center gap-2"
                  onClick={() => setShowAddToFolder(true)}
                >
                  <FolderPlus className="w-4 h-4" /> Add to Folder
                </button>
              )}
            </div>
          </div>

          <div className="prompt-action-row">
            <button
              type="button"
              className={`icon-action ${favorited ? 'active' : ''}`}
              onClick={handleToggleFavorite}
              data-tooltip="Favorite Prompt"
              aria-label="Favorite Prompt"
            >
              <Star className="w-5 h-5" />
            </button>
            <button
              type="button"
              className="icon-action"
              onClick={() => navigate('/prompts/create', {
                state: {
                  forkSource: prompt,
                  forkCreator: prompt?.creator
                }
              })}
              data-tooltip="Create Fork"
              aria-label="Create Fork"
            >
              <GitFork className="w-5 h-5" />
            </button>
            {isOwner && (
              <button
                type="button"
                className="icon-action"
                onClick={() => setShowShare(true)}
                data-tooltip="Share Prompt"
                aria-label="Share Prompt"
              >
                <Share2 className="w-5 h-5" />
              </button>
            )}
              <button
                type="button"
                className="icon-action danger"
                onClick={() => setShowReport(true)}
                data-tooltip="Report Prompt"
                aria-label="Report Prompt"
              >
                <AlertTriangle className="w-5 h-5" />
              </button>
              <button
                type="button"
                className={`icon-action ${activeVersion ? 'active' : ''}`}
                onClick={() => setShowHistory(true)}
                data-tooltip="Prompt History"
                aria-label="Prompt History"
              >
                <History className="w-5 h-5" />
              </button>
            </div>

          <div className="card-glass mt-6">
            <button
              type="button"
              className="w-full flex items-center justify-between text-left"
              onClick={() => setShowApiInfo((prev) => !prev)}
            >
              <div>
                <h3 className="text-lg font-semibold">API Access</h3>
                <span className="text-xs text-gray-500">Click to reveal endpoints.</span>
              </div>
              <span className="text-sm text-gray-400">{showApiInfo ? 'Hide' : 'Show'}</span>
            </button>
            {showApiInfo && (
              <div className="space-y-2 text-sm text-gray-300 mt-3">
                <div className="input-glass flex items-center justify-between gap-3">
                  <span className="break-all">{templateEndpoint}</span>
                  <button
                    className="icon-btn"
                    onClick={() => navigator.clipboard.writeText(templateEndpoint)}
                    data-tooltip="Copy"
                    aria-label="Copy template endpoint"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                <div className="input-glass flex items-center justify-between gap-3">
                  <span className="break-all">{outputsEndpoint}</span>
                  <button
                    className="icon-btn"
                    onClick={() => navigator.clipboard.writeText(outputsEndpoint)}
                    data-tooltip="Copy"
                    aria-label="Copy outputs endpoint"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="rating-block">
            <div className="rating-title">Prompt Rating</div>
            <div className="rating-score">
              {Number(prompt?.average_rating ?? 0).toFixed(1)} ({prompt?.rating_count ?? 0} Ratings)
            </div>
            <div className="rating-label">Rate this prompt:</div>
            <div className="rating-stars">
              {[1, 2, 3, 4, 5].map((value) => {
                const active = (hoverRating ?? userRating ?? 0) >= value;
                return (
                  <button
                    key={value}
                    type="button"
                    className={`rating-star ${active ? 'active' : ''}`}
                    onMouseEnter={() => setHoverRating(value)}
                    onMouseLeave={() => setHoverRating(null)}
                    onClick={() => handleSubmitRating(value)}
                  >
                    <Star className="w-5 h-5" />
                  </button>
                );
              })}
            </div>
          </div>

          {prompt?.forked_from && (
            <div className="forked-line">
              Prompt forked from{' '}
              <Link to={`/prompts/${prompt.forked_from.prompt_id}`} className="text-rebel-blue hover:underline">
                {prompt.forked_from.name}
              </Link>
              {prompt.forked_from.creator && (
                <span className="forked-creator">
                  <span>by</span>
                  <Link className="forked-link" to={buildProfilePath(prompt.forked_from.creator)}>
                    <span className="forked-avatar">
                      {prompt.forked_from.creator.avatar_url ? (
                        <img
                          src={prompt.forked_from.creator.avatar_url}
                          alt={prompt.forked_from.creator.username || prompt.forked_from.creator.name || 'Creator'}
                        />
                      ) : (
                        <span>
                          {(prompt.forked_from.creator.username || prompt.forked_from.creator.name || 'RP')
                            .slice(0, 2)
                            .toUpperCase()}
                        </span>
                      )}
                    </span>
                    <span>
                      {prompt.forked_from.creator.username || prompt.forked_from.creator.name || 'Rebel Creator'}
                    </span>
                  </Link>
                </span>
              )}
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="card-glass space-y-4">
            <div className="flex items-center gap-2 text-lg font-bold">
              <Sparkles className="w-5 h-5 text-rebel-blue" />
              Fill in the variables
            </div>
            {savedOutputs.length > 0 && (
              <div className="saved-outputs">
                <div className="saved-outputs-header">
                  <span className="text-sm font-semibold">Previously Saved</span>
                  <span className="text-xs text-gray-400">{savedOutputs.length} saves</span>
                </div>
                <SelectMenu
                  value={selectedSaveId}
                  options={savedOptions}
                  placeholder="Select a saved input set"
                  onChange={(value) => {
                    setSelectedSaveId(value);
                    const output = savedOutputs.find((item) => item.output_id === value);
                    applySavedOutput(output);
                  }}
                />
              </div>
            )}
            <div className="prompt-upload-panel">
              <div className="prompt-upload-header">
                <div>
                  <div className="text-sm font-semibold flex items-center gap-2">
                    <Paperclip className="w-4 h-4 text-rebel-blue" />
                    Private Prompt Files
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    Upload images or documents that stay attached only to your saved version of this prompt.
                  </p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(event) => mergePendingFiles(event.target.files)}
                />
                <button
                  type="button"
                  className="btn-secondary btn-compact flex items-center gap-2"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="w-4 h-4" /> Add Files
                </button>
              </div>

              {pendingFiles.length > 0 && (
                <div className="prompt-pending-files">
                  {pendingFiles.map((file, index) => (
                    <div key={`${file.name}-${file.size}-${file.lastModified}`} className="prompt-pending-file">
                      <div>
                        <div className="prompt-pending-file-name">{file.name}</div>
                        <div className="text-xs text-gray-400">
                          {file.type || 'Private file'} • {Math.max(1, Math.round(file.size / 1024))} KB
                        </div>
                      </div>
                      <button
                        type="button"
                        className="icon-btn"
                        onClick={() => removePendingFile(index)}
                        data-tooltip="Remove"
                        aria-label="Remove file"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <div className="flex flex-wrap gap-3">
                    {selectedSaveId && (
                      <button
                        type="button"
                        className="btn-secondary btn-compact"
                        onClick={handleUploadToSelectedSave}
                        disabled={uploadingFiles}
                      >
                        {uploadingFiles ? 'Uploading...' : 'Attach to Selected Save'}
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn-secondary btn-compact"
                      onClick={clearPendingFiles}
                      disabled={uploadingFiles}
                    >
                      Clear Files
                    </button>
                  </div>
                </div>
              )}

              <PromptAttachmentList
                attachments={selectedSavedOutput?.attachments || []}
                title={selectedSavedOutput ? 'Files on Selected Save' : 'Saved Files'}
                emptyLabel={selectedSaveId ? 'No files attached to this saved prompt yet.' : 'Select a saved prompt to view its private files.'}
                allowDelete={Boolean(selectedSaveId)}
                onDelete={handleDeleteAttachment}
              />
            </div>
            {effectivePrompt?.variables?.length ? (
              <div className="space-y-4">
                {effectivePrompt.variables.map((variable) => {
                  const value = inputValues[variable.variable_name];
                  const isMultiple = variable.input_type === 'multiple_choice';
                  const isDropdown = variable.input_type === 'dropdown';
                  const isMultiText = variable.input_type === 'multi_text';

                  return (
                    <div key={variable.variable_name}>
                      <label className="block text-sm font-medium mb-2">
                        {variable.display_name || variable.variable_name}
                      </label>
                      {variable.description && (
                        <p className="text-xs text-gray-400 mb-2">{variable.description}</p>
                      )}
                      {isDropdown && (
                        <SelectMenu
                          value={value || ''}
                          options={(variable.dropdown_options || []).map((opt) => ({
                            value: opt,
                            label: opt
                          }))}
                          placeholder="Select an option"
                          onChange={(next) => {
                            setInputValues((prev) => ({ ...prev, [variable.variable_name]: next }));
                          }}
                        />
                      )}
                      {isMultiple && (
                        <div className="flex flex-wrap gap-2">
                          {(variable.dropdown_options || []).map((opt) => {
                            const selected = Array.isArray(value) && value.includes(opt);
                            return (
                              <button
                                type="button"
                                key={opt}
                                className={`option-pill ${selected ? 'selected' : ''}`}
                                onClick={() => {
                                  setInputValues((prev) => {
                                    const current = Array.isArray(prev[variable.variable_name])
                                      ? prev[variable.variable_name]
                                      : [];
                                    const next = selected
                                      ? current.filter((item) => item !== opt)
                                      : [...current, opt];
                                    return { ...prev, [variable.variable_name]: next };
                                  });
                                }}
                              >
                                {opt}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {!isDropdown && !isMultiple && (
                        <>
                          {isMultiText ? (
                            <textarea
                              value={value || ''}
                              onChange={(event) => {
                                setInputValues((prev) => ({ ...prev, [variable.variable_name]: event.target.value }));
                              }}
                              className="input-glass"
                              rows="3"
                              placeholder={variable.default_value || 'Enter text'}
                            />
                          ) : (
                            <input
                              type={variable.input_type === 'number' ? 'number' : 'text'}
                              value={value || ''}
                              onChange={(event) => {
                                setInputValues((prev) => ({ ...prev, [variable.variable_name]: event.target.value }));
                              }}
                              className="input-glass"
                              placeholder={variable.default_value || 'Enter text'}
                            />
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-400">No variables were defined for this prompt.</p>
            )}
            <div className="flex flex-wrap gap-3">
              <button className="btn-primary" onClick={handleCompletePrompt}>
                Complete Prompt
              </button>
              <button
                className="btn-secondary btn-compact"
                onClick={() => setShowClearInputs(true)}
              >
                Clear Inputs
              </button>
            </div>
          </div>

          <div className="card-glass space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div className="text-lg font-bold">Generated Prompt</div>
              <span className="text-xs uppercase tracking-widest text-gray-500">Read Only</span>
            </div>
            <textarea
              className="input-glass font-mono"
              rows="12"
              readOnly
              value={completedPrompt || effectivePrompt?.prompt_template || ''}
            />
            <div className="flex flex-wrap gap-3">
              <button
                className="btn-secondary btn-compact flex items-center gap-2"
                onClick={handleCopy}
                disabled={!completedPrompt}
              >
                <Copy className="w-4 h-4" /> Copy to Clipboard
              </button>
              <button
                className="btn-secondary btn-compact flex items-center gap-2"
                onClick={handleRun}
                disabled={!completedPrompt}
              >
                <Play className="w-4 h-4" /> Run Prompt
              </button>
            </div>
            {completedPrompt && (
              <button className="btn-primary" onClick={handleSaveOutput} disabled={savingOutput || uploadingFiles}>
                {savingOutput || uploadingFiles
                  ? 'Saving...'
                  : pendingFiles.length
                    ? 'Save Prompt + Files'
                    : 'Save Prompt'}
              </button>
            )}
            {message && <p className="text-xs text-gray-400">{message}</p>}
          </div>
        </div>
      </div>

      <Modal
        open={showDelete}
        title="Move this prompt to trash?"
        onClose={() => setShowDelete(false)}
        footer={(
          <>
            <button className="btn-secondary btn-compact" onClick={() => setShowDelete(false)}>
              Cancel
            </button>
            <button className="btn-primary danger" onClick={handleDelete} disabled={actionLoading}>
              {actionLoading ? 'Moving...' : 'Move to Trash'}
            </button>
          </>
        )}
      >
        <p className="text-sm text-gray-400">
          The prompt will be moved to Trash and can be restored for 30 days.
        </p>
      </Modal>

      <Modal
        open={showAddToFolder}
        title="Add to your folders"
        onClose={() => setShowAddToFolder(false)}
        footer={(
          <>
            <button className="btn-secondary btn-compact" onClick={() => setShowAddToFolder(false)}>
              Cancel
            </button>
            <button className="btn-primary" onClick={handleAddToFolder} disabled={actionLoading}>
              {actionLoading ? 'Adding...' : 'Add Prompt'}
            </button>
          </>
        )}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Project</label>
            <SelectMenu
              value={selectedProjectId}
              options={projects.map((project) => ({
                value: project.project_id,
                label: project.name,
                description: project.description || 'Project'
              }))}
              placeholder="Select a project"
              onChange={(value) => {
                setSelectedProjectId(value);
                setSelectedFolderId('');
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Folder</label>
            <SelectMenu
              value={selectedFolderId}
              options={folders.map((folder) => ({
                value: folder.folder_id,
                label: folder.name,
                description: folder.description || 'Folder'
              }))}
              placeholder={selectedProjectId ? 'Select a folder' : 'Select a project first'}
              onChange={setSelectedFolderId}
            />
          </div>
          <p className="text-xs text-gray-400">
            This creates a private copy in your workspace. The original stays unchanged.
          </p>
        </div>
      </Modal>

      <Modal
        open={showHistory}
        title="Prompt History"
        onClose={() => setShowHistory(false)}
        footer={(
          <>
            <button className="btn-secondary btn-compact" onClick={() => setShowHistory(false)}>
              Close
            </button>
            <button
              className="btn-secondary btn-compact"
              onClick={() => {
                setActiveVersion(null);
                setInputValues(buildDefaultInputs(prompt));
                setCompletedPrompt('');
                setSelectedHistoryId('');
                setShowHistory(false);
              }}
            >
              Use Current
            </button>
            <button
              className="btn-primary"
              disabled={!selectedHistoryId}
              onClick={() => {
                const chosen = promptHistory.find((item) => item.history_id === selectedHistoryId);
                if (!chosen) return;
                setActiveVersion(chosen);
                setInputValues(buildDefaultInputs({
                  variables: chosen.variables_json || []
                }));
                setCompletedPrompt('');
                setShowHistory(false);
                setMessage(`Loaded ${chosen.action || 'UPDATED'} version for this session.`);
              }}
            >
              Use Selected Version
            </button>
          </>
        )}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">History versions</label>
            <SelectMenu
              value={selectedHistoryId}
              options={promptHistory.map((item) => ({
                value: item.history_id,
                label: formatHistoryLabel(item),
                description: item.name || 'Prompt version'
              }))}
              placeholder="Select a version"
              onChange={setSelectedHistoryId}
            />
          </div>
          {selectedHistoryId && (
            (() => {
              const chosen = promptHistory.find((item) => item.history_id === selectedHistoryId);
              if (!chosen) return null;
              return (
                <div className="space-y-3">
                  <div className="text-sm">
                    <span className="badge">{chosen.action || 'UPDATED'}</span>
                    <span className="ml-2 text-gray-400">{formatSavedDate(chosen.created_at)}</span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Prompt Name</label>
                    <input className="input-glass" value={chosen.name || ''} readOnly />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Template</label>
                    <textarea className="input-glass font-mono" rows="8" value={chosen.prompt_template || ''} readOnly />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Variables</label>
                    <div className="space-y-2">
                      {(chosen.variables_json || []).map((variable) => (
                        <div key={variable.variable_name} className="p-3 rounded-lg bg-white/5 border border-white/10">
                          <div className="font-medium">{variable.display_name || variable.variable_name}</div>
                          <div className="text-xs text-gray-400">{variable.description || 'No description provided.'}</div>
                        </div>
                      ))}
                      {!(chosen.variables_json || []).length && (
                        <div className="text-xs text-gray-400">No variables in this version.</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()
          )}
        </div>
      </Modal>

      <Modal
        open={showReport}
        title="Report this prompt"
        onClose={() => setShowReport(false)}
        footer={(
          <>
            <button className="btn-secondary btn-compact" onClick={() => setShowReport(false)}>
              Cancel
            </button>
            <button className="btn-primary" onClick={handleSubmitReport} disabled={actionLoading || !reportReason.trim()}>
              {actionLoading ? 'Submitting...' : 'Submit Report'}
            </button>
          </>
        )}
      >
        <div className="modal-field">
          <label>Reason</label>
          <textarea
            className="input-glass"
            rows="3"
            placeholder="Tell us why this prompt should be reviewed."
            value={reportReason}
            onChange={(event) => setReportReason(event.target.value)}
          />
        </div>
      </Modal>

      <ShareAccessModal
        open={showShare}
        onClose={() => setShowShare(false)}
        resourceType="prompt"
        resourceId={promptId}
        resourceName={prompt?.name}
        isOwner={isOwner}
      />

      <Modal
        open={showClearInputs}
        title="Clear all inputs?"
        onClose={() => setShowClearInputs(false)}
        footer={(
          <>
            <button className="btn-secondary btn-compact" onClick={() => setShowClearInputs(false)}>
              Cancel
            </button>
            <button
              className="btn-primary"
              onClick={() => {
                handleClearInputs();
                setShowClearInputs(false);
              }}
            >
              Clear Inputs
            </button>
          </>
        )}
      >
        <p className="text-sm text-gray-400">
          Are you sure you want to clear all inputs? This won’t delete any saved prompt inputs.
        </p>
      </Modal>
    </div>
  );
}

export default PromptDetail;
